import { createContext, useContext, type ReactNode } from 'react';
import type { Permission, PermissionRequirement } from '@/types/auth';
import type { AuthIdentity } from '@/types/identity';
import { hasPermission, meetsRequirement } from '@/utils/permissions';
import { useUser } from './UserContext';

interface RoleContextType {
  currentRole: string | null;
  roleDisplayName: string;
  user: AuthIdentity | null;
  isLoading: boolean;
}
const RoleContext = createContext<RoleContextType | undefined>(undefined);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useUser();
  // Derive authority in the same render; an effect would leave one stale render.
  const principal = loading || user?.must_change_password ? null : user;
  return <RoleContext.Provider value={{
    user: principal, currentRole: principal?.role || null,
    roleDisplayName: principal?.role_display_name || principal?.role || '',
    isLoading: loading,
  }}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextType {
  const context = useContext(RoleContext);
  if (!context) throw new Error('useRole must be used within a RoleProvider');
  return context;
}

export function usePermissions() {
  const context = useRole();
  return {
    ...context,
    hasPermission: (permission: Permission) => hasPermission(context.user, permission),
    meetsRequirement: (requirement: PermissionRequirement | undefined) => meetsRequirement(context.user, requirement),
  };
}
